import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { BoxTable } from "@/components/euro/BoxTable";
import { useEuroLive, periodLabel } from "@/hooks/useEuroLive";
import "@/components/standings/standings.css";
import "@/components/euro/euro.css";

const when = (iso) => new Date(iso).toLocaleString("en-GB", { timeZone: "Europe/Athens", weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
const qLabel = (i) => (i < 4 ? `Q${i + 1}` : `OT${i > 4 ? i - 3 : ""}`);

function Scoreboard({ comp, g, box, lv }) {
  const hp = lv ? lv.home_pts : g.played ? g.home_pts : box?.teams[0]?.totals.pts;
  const ap = lv ? lv.away_pts : g.played ? g.away_pts : box?.teams[1]?.totals.pts;
  const status = lv?.live ? <span className="eu-live"><em><i />LIVE</em>{[periodLabel(lv.quarter), lv.clock].filter(Boolean).join(" · ")}</span>
    : g.played || lv?.final ? <span className="eu-final-tag">FINAL</span> : <span>{when(g.date_utc)}</span>;
  const team = (c, pts, win) => (
    <Link to={`/europe/${comp}/teams/${c.code}`} className={`eu-sb-team ${win ? "w" : ""}`}>
      {c.crest && <img src={c.crest} alt="" />}<strong>{c.name}</strong><b>{pts ?? "–"}</b>
    </Link>
  );
  return (
    <header className="eu-sb" data-testid="europe-scoreboard">
      <div className="ncs-eyebrow">{comp === "E" ? "EUROLEAGUE" : "EUROCUP"} · {g.round_name}{g.group && g.group !== "Regular Season" ? ` · GROUP ${g.group}` : ""}</div>
      <div className="eu-sb-row">
        {team(g.home, hp, hp > ap)}
        <div className="eu-sb-status" data-testid="europe-game-status">{status}</div>
        {team(g.away, ap, ap > hp)}
      </div>
      <p className="eu-sb-meta">{[g.venue, box?.attendance && `Attendance ${Number(box.attendance).toLocaleString("en-GB")}`, box?.referees?.length && `Referees ${box.referees.join(", ")}`].filter(Boolean).join(" · ")}</p>
    </header>
  );
}

function Quarters({ g, box }) {
  const n = Math.max(...box.teams.map((t) => t.quarters.length), 4);
  return (
    <section className="ncs-panel" data-testid="europe-quarters">
      <h2><span className="ncs-east" />SCORE BY PERIOD</h2>
      <div className="ncs-scroll">
        <table className="eu-q-table">
          <thead><tr><th className="ncs-team-col">TEAM</th>{Array.from({ length: n }, (_, i) => <th key={i}>{qLabel(i)}</th>)}<th>TOTAL</th></tr></thead>
          <tbody>
            {[g.home, g.away].map((c, i) => (
              <tr key={c.code} data-testid={`europe-quarters-${i ? "away" : "home"}`}>
                <td className="ncs-team-col"><span className="eu-player">{c.crest && <img src={c.crest} alt="" className="eu-q-crest" />}{c.short}</span></td>
                {Array.from({ length: n }, (_, q) => <td key={q}>{box.teams[i]?.quarters[q] ?? "–"}</td>)}
                <td className="eu-box-pts">{box.teams[i]?.totals.pts}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function EuroGame() {
  const { comp, gameId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const live = useEuroLive();
  const lv = live[gameId];
  const load = useCallback(() => api.get(`/euro/${comp}/games/${gameId}`).then((r) => setData(r.data)).catch((e) => setError(errorText(e))), [comp, gameId]);
  useEffect(() => { setData(null); setError(""); load(); }, [load]);
  const isLive = lv?.live || data?.box?.live;
  useEffect(() => {
    if (!isLive) return undefined;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [isLive, load]);

  return (
    <div className="ncs-root eu-root" data-testid="europe-game-page">
      <Link to={`/europe/${comp}`} className="eu-back" data-testid="europe-game-back"><ArrowLeft size={15} />{comp === "E" ? "EuroLeague" : "EuroCup"}</Link>
      {error && <p className="ncs-state" role="alert" data-testid="europe-game-error">{error}</p>}
      {!data && !error && <p className="ncs-state" data-testid="europe-game-loading">Loading game…</p>}
      {data && (
        <>
          <Scoreboard comp={comp} g={data.game} box={data.box} lv={lv} />
          {data.box ? (
            <>
              <Quarters g={data.game} box={data.box} />
              <BoxTable club={data.game.home} team={data.box.teams[0]} side="home" live={isLive} />
              <BoxTable club={data.game.away} team={data.box.teams[1]} side="away" live={isLive} />
              <p className="ncs-footnote">Official {comp === "E" ? "EuroLeague" : "EuroCup"} box score.{isLive ? " Refreshes every 30 seconds while the game is live." : ""} Hover a shooting cell for the percentage.</p>
            </>
          ) : <p className="ncs-state" data-testid="europe-game-nobox">{data.game.played || lv ? "Box score temporarily unavailable — try again in a minute." : "The box score appears here once the game tips off."}</p>}
        </>
      )}
    </div>
  );
}
